import { Children, type ReactNode, useEffect, useRef, useState } from "react";

import { cn } from "@virtual-brain-lib/frontend-design-system/utils";

import { ToolCardContextProvider, type ToolCardLayoutMode } from "./ToolCardContext";

const MIN_CARD_WIDTH_PX = 224;
const GAP_PX = 8;
const EXPANDED_MIN_WIDTH_PX = 2 * MIN_CARD_WIDTH_PX + GAP_PX;

export interface ToolCardGroupProps {
	readonly children: ReactNode;
	readonly className?: string;
}

export function ToolCardGroup({ children, className }: ToolCardGroupProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [mode, setMode] = useState<ToolCardLayoutMode>("expanded");

	useEffect(() => {
		const node = containerRef.current;

		if (!node) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect.width ?? 0;

			setMode(width < EXPANDED_MIN_WIDTH_PX ? "compact" : "expanded");
		});

		observer.observe(node);

		return () => {
			observer.disconnect();
		};
	}, []);

	if (Children.toArray(children).length === 0) {
		return null;
	}

	return (
		<ToolCardContextProvider value={{ mode }}>
			<div
				ref={containerRef}
				className={cn(
					"w-full max-w-180 gap-2",
					mode === "compact"
						? "flex flex-col *:min-w-0 *:flex-1"
						: "grid grid-cols-2 *:only:col-span-2",
					className,
				)}
			>
				{children}
			</div>
		</ToolCardContextProvider>
	);
}
